import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import {
  Button,
  Toolbar,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';

import ContentBox from '../../components/layout/ContentBox';
import InstituteEditDialog from '../../components/institute/InstituteEditDialog';
import ZerpaTable, { ColumnObject, ZerpaTableStateSetters } from '../../components/layout/table/ZerpaTable';
import ZerpaSearch from '../../components/layout/ZerpaSearch';
import Prompt from '../../components/layout/Prompt';

import usePrevious from '../../hooks/usePrevious';
import { useCheckboxRedux } from '../../components/layout/table/useCheckbox';
import {
  showNotification,
  showErrorNotification,
  updateInstitute,
  navigateTo,
  fetchInstitutes,
  fetchUsers,
  updateReduxInstitute,
  addInstitute,
  deleteInstitute,
  setPageSearchText,
  setPageFilterValue,
  approveInstitutes,
  setZerpaTableState,
  setLoading,
} from '../../redux/actions';
import {
  Institute,
  QuipUser,
  RootState,
  ZerpaState,
} from '../../redux/types';

type Props = PropsFromRedux;

type Operations = 'edit' | 'add' | 'delete';

type InstituteUserCount = {
  [instituteId: number]: number | undefined;
}

type ExtendedInstitute = Institute & {
  userCount: number | undefined;
}

const pageIdentifier = 'institutemanagement';

const columns: ColumnObject[] = [
  {
    keyName: '',
    text: 'select',
    type: 'checkbox',
  },
  {
    keyName: 'id',
    text: 'Institute Id',
    sortable: true,
  },
  {
    keyName: 'name',
    text: <FormattedMessage id="trialSetup.instituteNameColumn" />,
    sortable: true,
  },
  {
    keyName: 'hospitalName',
    text: <FormattedMessage id="registration.hospitalName" />,
    sortable: true,
  },
  {
    keyName: 'headOfInstitute',
    text: <FormattedMessage id="registration.userPositionDirector" />,
    sortable: true,
  },
  {
    keyName: 'city',
    text: <FormattedMessage id="trialSetup.instituteCityColumn" />,
    sortable: true,
  },
  {
    keyName: 'zip',
    text: <FormattedMessage id="registration.instituteZip" />,
    sortable: true,
  },
  {
    keyName: 'country',
    text: <FormattedMessage id="registration.instituteCountry" />,
    sortable: true,
  },
  {
    keyName: 'userCount',
    text: <FormattedMessage id="menu.users" />,
    sortable: true,
  },
];

const InstituteManagementView: React.FC<Props> = ({
  locale,
  showNotification: showNotificationAction,
  showErrorNotification: showErrorNotificationAction,
  updateInstitute: updateInstituteAction,
  fetchInstitutes: fetchInstitutesAction,
  fetchUsers: fetchUserAction,
  addInstitute: addInstituteAction,
  deleteInstitute: deleteInstituteAction,
  setPageSearchText: setPageSearchTextAction,
  setPageFilterValue: setPageFilterValueAction,
  approveInstitutes: approveInstitutesAction,
  setZerpaTableState: setZerpaTableStateAction,
  setLoading: setLoadingAction,
  institutesById,
  instituteAllIds,
  usersById,
  searchText,
  filterValue,
  zerpaTableState,
}: Props) => {
  const checkboxSelect = useCheckboxRedux(pageIdentifier);
  const prevFilterState = usePrevious(filterValue);
  const prevSearchText = usePrevious(searchText);
  const zerpaTableSetters = useRef<ZerpaTableStateSetters>();
  const [instituteList, setInstituteList] = useState<ExtendedInstitute[]>();
  const [operableInstitutes, setOperableInstitute] = useState<Institute[]>();
  const [operation, setOperation] = useState<Operations>();
  const [editButton, showEditButton] = useState<boolean>(false);
  const navigate = useNavigate();

  /**
   * Constructs a map of instituteIds with number of users
   * belonging to that institute
  */
  const getUsersCountInInstitute = useCallback((): InstituteUserCount|undefined => {
    if (!usersById) {
      return undefined;
    }
    const instituteUserCounts: InstituteUserCount = {};
    Object.values(usersById).forEach((user: QuipUser) => {
      const usersInstituteId = user.instituteId;
      const count = instituteUserCounts[usersInstituteId];
      if (!count) {
        instituteUserCounts[usersInstituteId] = 1;
      } else {
        instituteUserCounts[usersInstituteId] = count + 1;
      }
    });
    return instituteUserCounts;
  }, [usersById]);

  /**
   * This callback runs when zerpa table internal state updates
   * Callback maintains the zerpa table internal states in redux store
   * and keeps the reference of the internal state setters in the ref
   *
   * @param zerpaState Internal zerpa state
   * @param stateSetters object containing internal zerpa table state setters
   */
  const onZerpaTableStateChange = useCallback((
    zerpaState: ZerpaState,
    stateSetters?: ZerpaTableStateSetters,
  ) => {
    setZerpaTableStateAction(pageIdentifier, zerpaState);
    if (!zerpaTableSetters.current && stateSetters) {
      zerpaTableSetters.current = stateSetters;
    }
  }, [setZerpaTableStateAction]);

  useEffect(() => {
    setLoadingAction(true);
    fetchInstitutesAction();
    fetchUserAction();
  }, [fetchInstitutesAction, fetchUserAction, setLoadingAction]);

  useEffect(() => {
    if (institutesById !== undefined && usersById !== undefined) {
      setLoadingAction(false);
    }
  }, [institutesById, usersById, setLoadingAction]);

  useEffect(() => {
    if (checkboxSelect.selectedIds.length === 1) {
      showEditButton(true);
    } else {
      showEditButton(false);
    }
  }, [checkboxSelect.selectedIds]);

  /**
   * construct list of institutes based on search text
   * and filter parameters to pass the list to zerpa table
   */
  useEffect(() => {
    if (!institutesById || !instituteAllIds) {
      return;
    }
    const allInstitutes = instituteAllIds.map((id) => institutesById[id]);
    let filteredInstitutes = allInstitutes;
    if (searchText) {
      const trimmedSearchText = searchText.toLowerCase().trim();
      filteredInstitutes = allInstitutes.filter((val: Institute) => (
        val.name?.toLowerCase().includes(trimmedSearchText)
        || val.headOfInstitute?.toLowerCase().includes(trimmedSearchText)
        || val.hospitalName?.toLocaleLowerCase().includes(trimmedSearchText)
        || val.city?.toLocaleLowerCase().includes(trimmedSearchText)
        || val.id.toString().includes(trimmedSearchText)
        || val.zip.toString().includes(trimmedSearchText)
      ));
    }
    if (filterValue && filterValue === 'pendingApproval') {
      filteredInstitutes = filteredInstitutes.filter((val: Institute) => val.pendingApproval);
    }
    if (filterValue && filterValue === 'currentSelected') {
      filteredInstitutes = filteredInstitutes
        .filter((institute) => (checkboxSelect.selectedIds as number[]).includes(institute.id));
    }
    // compute user counts
    const userCounts = getUsersCountInInstitute();
    const extendedInstitutes = filteredInstitutes.map((inst) => ({
      ...inst,
      userCount: userCounts && userCounts[inst.id],
    } as ExtendedInstitute));
    setInstituteList(extendedInstitutes);
  }, [
    instituteAllIds,
    institutesById,
    searchText,
    filterValue,
    checkboxSelect.selectedIds,
    getUsersCountInInstitute,
  ]);

  useEffect(() => {
    const zerpaSetters = zerpaTableSetters.current;
    if (
      filterValue
        && (filterValue === 'pendingApproval' || filterValue === 'currentSelected')
        && prevFilterState !== filterValue
        && zerpaSetters
    ) {
      const { setPage } = zerpaSetters;
      setPage(0);
    }
  }, [filterValue, prevFilterState]);

  /** Set page to 0 on search text changes */
  useEffect(() => {
    const zerpaSetters = zerpaTableSetters.current;
    if (zerpaSetters) {
      const { setPage } = zerpaSetters;
      if (prevSearchText !== undefined && prevSearchText !== searchText) {
        setPage(0);
      }
    }
  }, [searchText, prevSearchText]);

  const getSelectedInstitutes = (): Institute[] | undefined => {
    if (!institutesById) {
      return undefined;
    }
    return checkboxSelect.selectedIds.map((id) => institutesById[id as number]);
  };

  const onAddInstitute = () => {
    setOperableInstitute([{} as Institute]);
    setOperation('add');
  };

  const onEditInstitute = () => {
    const selectedInstitutes = getSelectedInstitutes();
    if (selectedInstitutes) {
      setOperableInstitute([selectedInstitutes[0]]);
      setOperation('edit');
    }
  };

  const onDeleteInstitute = () => {
    const selectedInstitutes = getSelectedInstitutes();
    if (selectedInstitutes) {
      setOperableInstitute(selectedInstitutes);
      setOperation('delete');
    }
  };

  const onShowUsers = () => {
    const selectedInstitute = getSelectedInstitutes();
    if (selectedInstitute) {
      navigateTo(`/institutemanagement/${selectedInstitute[0].id}/users`, navigate);
    }
  };

  const onCloseDialog = (clearCheckboxes: boolean) => {
    setOperableInstitute(undefined);
    setOperation(undefined);
    if (clearCheckboxes) {
      checkboxSelect.clearSelectedIds();
    }
  };

  const onSearchTextChange = (text: string) => {
    setPageSearchTextAction(pageIdentifier, text);
  };

  const onChangeFilter = (event: SelectChangeEvent<string>) => {
    setPageFilterValueAction(pageIdentifier, event.target.value as string);
  };

  const addInstituteHandler = async (institute: Institute) => {
    addInstituteAction(institute);
  };

  const onConfirmDeleteInstitute = async (confirm: boolean) => {
    if (!confirm) {
      onCloseDialog(false);
      return;
    }
    if (!operableInstitutes) {
      return;
    }
    try {
      await deleteInstituteAction(operableInstitutes[0]);
      onCloseDialog(true);
      showNotificationAction(<FormattedMessage id="groundtruthview.submittedGroundTruth" />);
      checkboxSelect.clearSelectedIds();
    } catch (e) {
      showErrorNotificationAction((e as Error).message);
    }
  };

  const approveInstitutesHandler = () => {
    if (!institutesById) {
      return;
    }
    const instIds = checkboxSelect.selectedIds as number[];
    const pendingInstIds = instIds.filter((ids) => institutesById[ids].pendingApproval);
    try {
      approveInstitutesAction(pendingInstIds);
      checkboxSelect.clearSelectedIds();
      showNotificationAction(<FormattedMessage id="genericSaveMessage" />);
    } catch (err) {
      showErrorNotificationAction('Error during approving institutes');
    }
  };

  return (
    <>
      <ContentBox
        title={<FormattedMessage id="instituteManagement.instituteManagement" />}
        elevation={0}
      >
        <Toolbar disableGutters>
          {
            checkboxSelect.selectedIds.length
              ? (
                <div style={{ flexGrow: 1 }}>
                  <Button sx={{ mx: 0.5 }} variant="outlined" onClick={checkboxSelect.clearSelectedIds}><FormattedMessage id="resetPassword.buttonLabel" /></Button>
                </div>
              )
              : <Typography variant="h5" style={{ flexGrow: 1 }}><FormattedMessage id="instituteManagement.registerdInstitutes" /></Typography>
          }
          {
            checkboxSelect.selectedIds.length === 0
            && <Button sx={{ mx: 0.5 }} variant="outlined" onClick={onAddInstitute}><FormattedMessage id="add" /></Button>
          }
          {
            editButton
            && checkboxSelect.selectedIds.length
            && (
              <>
                <Button sx={{ mx: 0.5 }} variant="outlined" onClick={onShowUsers}><FormattedMessage id="instituteManagement.showUsers" /></Button>
                <Button sx={{ mx: 0.5 }} variant="outlined" onClick={onDeleteInstitute}><FormattedMessage id="genericDelete" /></Button>
                <Button sx={{ mx: 0.5 }} variant="outlined" onClick={onEditInstitute}><FormattedMessage id="edit" /></Button>
              </>
            )
          }
          {
            filterValue === 'pendingApproval'
            && checkboxSelect.selectedIds.length > 0
            && (
              <Button sx={{ mx: 0.5 }} variant="outlined" onClick={approveInstitutesHandler}><FormattedMessage id="approve" /></Button>
            )
          }
        </Toolbar>
        <Toolbar variant="dense" disableGutters>
          <ZerpaSearch fullWidth value={searchText} onTextChange={onSearchTextChange} />
          <FormControl sx={{ m: 1, minWidth: 200 }} variant="outlined" size="small">
            <InputLabel id="demo-simple-select-label">Filter by</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={filterValue}
              onChange={onChangeFilter}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="pendingApproval">Pending Approval</MenuItem>
              <MenuItem value="currentSelected">Selected</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
        <div style={{ overflow: 'auto' }}>
          {
            instituteList && (
              <ZerpaTable<ExtendedInstitute>
                keyIdentifier="id"
                columns={columns}
                tableRows={instituteList}
                showPagination
                dense
                checkboxSelection={checkboxSelect}
                getZerpaState={onZerpaTableStateChange}
                zerpaState={zerpaTableState}
                multiselect={false}
              />
            )
          }
        </div>
      </ContentBox>
      {
        /** Institute Add|Edit Dialog */
        !!operation && !!operableInstitutes
        && (
          <InstituteEditDialog
            isOpen={!!operation && (operation === 'edit' || operation === 'add')}
            institute={operableInstitutes[0]}
            updateInstitute={operation === 'edit' ? updateInstituteAction : addInstituteHandler}
            showNotification={showNotificationAction}
            onClose={() => { onCloseDialog(false); }}
            showForwardAddress
            locale={locale}
          />
        )
      }
      {
        /** Delete confirmation Prompt */
        !!operation
        && (
          <Prompt
            display={operation === 'delete'}
            title="Delete Institute"
            action={onConfirmDeleteInstitute}
          >
            <FormattedMessage id="instituteManagement.confirmDelete" />
          </Prompt>
        )
      }
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  locale: state.intl.locale,
  institutesById: state.institute.byId,
  instituteAllIds: state.institute.allIds,
  usersById: state.users.byId,
  searchText: state.pageState.institutemanagement.searchText,
  filterValue: state.pageState.institutemanagement.filterValue,
  zerpaTableState: state.pageState.institutemanagement.zerpaTableState,
});

const dispatchProps = {
  showNotification,
  showErrorNotification,
  updateInstitute,
  fetchInstitutes,
  fetchUsers,
  updateReduxInstitute,
  addInstitute,
  deleteInstitute,
  setPageSearchText,
  setPageFilterValue,
  approveInstitutes,
  setZerpaTableState,
  setLoading,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(InstituteManagementView);
